import { ofType } from 'redux-observable';
import { Observable, EMPTY, of } from 'rxjs';
import { take, filter, map, mergeMap, catchError } from 'rxjs/operators';
import get from 'lodash.get';
import React from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';

import { NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_INFO } from 'ui-components/services/notification-service';
import { NotificationHubService } from '../notification-hub/notification-hub-service';
import { NotificationType } from '../notification-hub/notification-hub.types';
import { notificationDescriptionMessages } from '../notification-hub/notification-messages';

import GlobalSelectors from '../../store/global-selectors';
import { PusherService } from '../../services/pusher-service';
import {
  EXPORT_FULFILLMENT_EXPORT_JOB_COMPLETE,
  CORE_JOBSERVICE_JOB_COMPLETE,
} from '../../services/pusher-service/pusher-contract-messages';
import { DocumentService } from '../../services/document-service';
import * as BulkActions from '../../store/bulk/bulk.actions';
import * as FulfillmentsApi from '../../shipping/fulfillments/fulfillments.api';
import * as Actions from './external-fulfillments.actions';
import { FulfillmentExportHelper } from './export-helper';
import { messages } from './messages';

const exportFailedMessage = defineMessage({
  description: 'Error message when trying to export',
  defaultMessage: 'Error exporting fulfillments. Please try again.',
  id: 'etCFMq',
});

const NOTIFICATION_DURATION_IN_MS = 10000;

const showSuccessNotification = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.FULFILLMENTS_EXPORT_STARTED,
      defaultTitle: messages.fulfillmentExportNotifHeader.defaultMessage,
      defaultDescription: notificationDescriptionMessages.FULFILLMENTS_EXPORT_STARTED.defaultMessage,
    },
    {
      notificationType: NOTIFICATION_TYPE_INFO,
      title: <FormattedMessage {...messages.fulfillmentExportNotifHeader} />,
      content: <FormattedMessage {...messages.fulfillmentExportNotifBody} />,
      duration: NOTIFICATION_DURATION_IN_MS,
    },
  );
};

const showFailureNotification = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.FULFILLMENTS_EXPORT_FAILED,
      defaultTitle: messages.fulfillmentExportErrorHeader.defaultMessage,
      defaultDescription: notificationDescriptionMessages.FULFILLMENTS_EXPORT_FAILED.defaultMessage,
    },
    {
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.fulfillmentExportErrorHeader} />,
      content: <FormattedMessage {...messages.fulfillmentExportErrorBody} />,
      duration: NOTIFICATION_DURATION_IN_MS,
    },
  );
};

const showDoneNotification = (downloadUrl) => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.FULFILLMENTS_EXPORT_COMPLETED,
      defaultTitle: messages.fulfillmentExportNotifDoneHeader.defaultMessage,
      defaultDescription: notificationDescriptionMessages.FULFILLMENTS_EXPORT_COMPLETED.defaultMessage,
      metadataJson: JSON.stringify({ downloadUrl }),
    },
    {
      notificationType: NOTIFICATION_TYPE_INFO,
      title: <FormattedMessage {...messages.fulfillmentExportNotifDoneHeader} />,
      content: <FormattedMessage {...messages.fulfillmentExportNotifDoneBody} />,
      duration: NOTIFICATION_DURATION_IN_MS,
    },
  );
};

export const handleReceivedPusherMessage = (action$, { getState }) =>
  action$.pipe(
    ofType(BulkActions.BULK_PROCESSING_CREATE_LABEL_MESSAGE),
    mergeMap((action) => {
      const state = getState();
      const message = get(action, 'message');
      const hasErrors = get(message, 'status.Errors', 0) > 0;
      const batchId = get(message, 'status.BatchID', null);
      const batchGroupKey = GlobalSelectors.selectBulkBatchGroupIdFromBatchId(state, { batchId });
      const batchGroup = GlobalSelectors.selectBulkBatchGroupByKey(state, {
        batchGroupKey,
      });
      const isExternalBatch = get(batchGroup, 'metadata.isExternalFulfillment', false);
      if (!batchGroup) {
        return EMPTY;
      }
      if (hasErrors && isExternalBatch) {
        return of(BulkActions.bulkProcessingUnknownError(batchId, message));
      }
      return EMPTY;
    }),
  );

const notifyExportError = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.FULFILLMENTS_EXPORT_FAILED,
      defaultTitle: messages.fulfillmentExportErrorHeader.defaultMessage,
      defaultDescription: notificationDescriptionMessages.FULFILLMENTS_EXPORT_FAILED.defaultMessage,
    },
    {
      notificationType: NOTIFICATION_TYPE_ERROR,
      content: <FormattedMessage {...exportFailedMessage} />,
      duration: 3000,
    },
  );
};

export const onExportFulfillment = (action$) => {
  return action$.pipe(
    ofType(Actions.EXPORT_FULFILLMENT_REQUEST),
    mergeMap((action) => {
      return FulfillmentsApi.exportFulfillments(action.fulfillmentIds, action.filters).pipe(
        mergeMap((data) => {
          const jobGuid = data.jobGuids[0];

          return new Observable((observer) => {
            const onMessage = (message) => observer.next(message);
            const unbindFromEvent = PusherService.bindToEvent(CORE_JOBSERVICE_JOB_COMPLETE, onMessage);
            return () => unbindFromEvent();
          }).pipe(
            filter((pusherMessage) => pusherMessage.jobId === jobGuid),
            take(1),
            map((pusherData) => pusherData.Notification.Message),
            map((msg) => {
              const hrefPattern = /href=".*"/g;
              return msg.match(hrefPattern)[0];
            }),
            map((href) => {
              // strip href= and nested quotes
              return href.substring(href.indexOf('=') + 2, href.length - 1);
            }),
            mergeMap((url) => {
              const downloadUrl = url.trim();
              showDoneNotification(downloadUrl);
              DocumentService.downloadFile(downloadUrl);
              return EMPTY;
            }),
          );
        }),
        catchError(() => {
          notifyExportError();
          return EMPTY;
        }),
      );
    }),
  );
};

export const onExportFulfillmentExtended = (action$) => {
  return action$.pipe(
    ofType(Actions.EXPORT_FULFILLMENTS_REQUEST_EXTENDED),
    mergeMap((action) => {
      const {
        viewRecordsOrSelectedRecords,
        primaryRecordsOrLineItems,
        selectedFulfillmentIds,
        orderByColumn,
        orderByDirection,
        columnConfiguration,
        quickSearchTerm,
        intl,
        filters,
      } = action;
      const { requestUrl, requestBody } = FulfillmentExportHelper.getExportRequestParameters(
        viewRecordsOrSelectedRecords,
        primaryRecordsOrLineItems,
        selectedFulfillmentIds,
        orderByColumn,
        orderByDirection,
        columnConfiguration,
        quickSearchTerm,
        intl,
        filters,
      );
      return FulfillmentsApi.exportFulfillmentsExtended(requestUrl, requestBody).pipe(
        mergeMap((data) => {
          showSuccessNotification();
          const jobGuid = data;
          return new Observable((observer) => {
            const onMessage = (message) => observer.next(message);
            const unbindFromEvent = PusherService.bindToEvent(EXPORT_FULFILLMENT_EXPORT_JOB_COMPLETE, onMessage);
            return () => unbindFromEvent();
          }).pipe(
            filter((pusherMessage) => pusherMessage.messageGuid === jobGuid),
            take(1),
            map((pusherData) => pusherData.data.DownloadUrl),
            mergeMap((url) => {
              const downloadUrl = url.trim();
              showDoneNotification(downloadUrl);
              DocumentService.downloadFile(downloadUrl);
              return EMPTY;
            }),
          );
        }),
        catchError(() => {
          showFailureNotification();
          return EMPTY;
        }),
      );
    }),
  );
};

export const onCustomExportFulfilments = (action$) => {
  return action$.pipe(
    ofType(Actions.CUSTOM_EXPORT_FULFILLMENTS_REQUEST),
    mergeMap((action) => {
      const { viewRecordsOrSelectedRecords, extractId, selectedFulfillmentIds, orderByColumn, orderByDirection, searchTerm, filters } =
        action;
      const { requestBody, requestUrl } = FulfillmentExportHelper.getCustomExportRequestParameters(
        viewRecordsOrSelectedRecords,
        extractId,
        selectedFulfillmentIds,
        orderByColumn,
        orderByDirection,
        searchTerm,
        filters,
      );
      return FulfillmentsApi.customExportFulfillments(requestBody, requestUrl).pipe(
        mergeMap((data) => {
          showSuccessNotification();
          const jobGuid = data.jobGuids[0];
          return new Observable((observer) => {
            const onMessage = (message) => observer.next(message);
            const unbindFromEvent = PusherService.bindToEvent(EXPORT_FULFILLMENT_EXPORT_JOB_COMPLETE, onMessage);
            return () => unbindFromEvent();
          }).pipe(
            filter((pusherMessage) => pusherMessage.messageGuid === jobGuid),
            take(1),
            map((pusherData) => pusherData.data.DownloadUrl),
            mergeMap((url) => {
              const downloadUrl = url.trim();
              showDoneNotification(downloadUrl);
              DocumentService.downloadFile(downloadUrl);
              return EMPTY;
            }),
          );
        }),
        catchError(() => {
          showFailureNotification();
          return EMPTY;
        }),
      );
    }),
  );
};
